import PropTypes from 'prop-types';
import { SliceQuestion } from './SliceQuestion/index';
import { SliceInfo } from './SliceInfo/index';
import { GAMEMODE, QUESTION_TYPE } from '../../utils/constants';
import useVideo from '../../hooks/useVideo';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { finishGame, setShowBankControl } from '@educaplay/store/slices/gameSlice';
import { useSelector } from 'react-redux';
import useSlice from '../../hooks/useSlice';
import classes from './Slices.module.scss';
import useVideoQuizConfig from '../../hooks/useVideoQuizConfig';
import { SCREENS } from '@educaplay/store/constants';

const DESAPPEAR_DELAY = 700;

export function Slices(props) {
    const { questions, onAnswer, onPassQuestion } = props;
    const currentQuestion = useSelector(state => questions[state.game.currentIndex]);
    const isAnimating = useSelector(state => state.game.isAnimating);
    const { gameMode } = useVideoQuizConfig();
    const { pause, play, progress, ref } = useVideo();
    const { setShowSlice, showSlice, hasViewed, setHasViewed, currentQuestionTimeAppear } = useSlice();
    const dispatch = useDispatch();
    const totalDuration = currentQuestion.totalDuration;
    const nextQuestion = questions.findIndex(q => q.id === currentQuestion.id) + 1 < questions.length ? questions[currentQuestion.id + 1] : null;
    const nextQuestionTimeAppear = nextQuestion?.timeAppearWithDuration ?? nextQuestion?.timeAppear;
    const type = currentQuestion.questionType;
    const isInfoScreen = type === QUESTION_TYPE.Info;
    const isQuestionScreen = type === QUESTION_TYPE.MultiQuestion || type === QUESTION_TYPE.SingleQuestion || type === QUESTION_TYPE.TextQuestion;
    const hasDuration = currentQuestion.timeAppearWithDuration !== null;
    const view = useSelector(state => state.game.view);

    useEffect(() => {
        if (currentQuestionTimeAppear === progress) return;
        setHasViewed(false);
    }, [progress, currentQuestionTimeAppear, setHasViewed])

    const onFinishSliceQuestion = (userAnswer) => {
        onAnswer(userAnswer);
        if (nextQuestionTimeAppear === currentQuestionTimeAppear) return;
        setHasViewed(true);
        setTimeout(() => {
            setShowSlice(false);
            dispatch(setShowBankControl(false));
        }, DESAPPEAR_DELAY);
    }

    useEffect(() => {
        if (!isAnimating && !showSlice && progress < totalDuration) {
            play();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnimating, showSlice, progress])

    const appearSlices = useCallback(() => {
        setShowSlice(true);
        dispatch(setShowBankControl(!isInfoScreen));
        setTimeout(() => {
            pause();
        },500);
    }, [dispatch, isInfoScreen, pause, setShowSlice])

    const appearPrevSlices = useCallback(() => {
        setShowSlice(isInfoScreen);
        dispatch(setShowBankControl(!isInfoScreen));
    }, [dispatch, isInfoScreen, setShowSlice])

    useEffect(() => {
        if (hasViewed) return;

        if (hasDuration && progress >= currentQuestion.timeAppear && progress < currentQuestion.timeAppearWithDuration) {
            appearPrevSlices();
            return;
        }

        if (currentQuestionTimeAppear === progress) {
            appearSlices();
            return;
        }

        if (progress >= totalDuration && !showSlice) {
            pause();
            if (gameMode === GAMEMODE.FREEDOM) {
                ref.current.seekTo(totalDuration);
                return;
            }
            if (gameMode === GAMEMODE.SEQUENTIAL && view === SCREENS.GAME) {
                const finishParams = questions.every((question) => question.questionType === QUESTION_TYPE.Info) ? { points: 100 } : null;
                dispatch(finishGame(finishParams))
                return;
            }
        }

        setShowSlice(false);
        dispatch(setShowBankControl(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion.timeAppear, currentQuestion.timeAppearWithDuration, progress, hasViewed, totalDuration, showSlice, view])


    return (
        <div
            className={classes.container}
            onClick={(e) => {
                e.stopPropagation();
                onPassQuestion();
            }}
            style={{ pointerEvents: showSlice && progress === currentQuestionTimeAppear ? "auto" : "none" }}
        >
            <SliceInfo show={isInfoScreen && showSlice} currentQuestion={currentQuestion} />
            <SliceQuestion
                key={currentQuestion.id}
                show={isQuestionScreen && showSlice}
                onAnswer={onFinishSliceQuestion}
                currentQuestion={currentQuestion}
                typeOfQuestion={currentQuestion.questionType}
            />
        </div>
    )
}

Slices.propTypes = {
    onAnswer: PropTypes.func,
    questions: PropTypes.array,
}
